/**
 * AppCard component - displays an application card for the dashboard
 *
 * Shows app icon, title, description and links to the application route.
 *
 * Validates: Requirements 1.3
 */

import React from 'react';
import { ArrowRightRegular } from '@fluentui/react-icons';
import styles from './AppCard.module.css';

export interface AppCardProps {
  /** Unique identifier for the app */
  id: string;
  /** Display title for the app */
  title: string;
  /** Description of what the app does */
  description: string;
  /** URL to navigate to when clicked */
  href: string;
  /** Icon component to display */
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Application card component for the landing page dashboard
 *
 * Displays an app with its icon, title, and description.
 * Clicking the card navigates to the application.
 */
export const AppCard: React.FC<AppCardProps> = ({
  id,
  title,
  description,
  href,
  icon: Icon,
}) => {
  return (
    <a
      href={href}
      className={styles.card}
      data-testid={`app-card-${id}`}
      aria-label={`Open ${title}`}
    >
      <div className={styles.iconContainer}>
        <Icon className={styles.icon} />
      </div>
      <h3 className={styles.title}>{title}</h3>
      <p className={styles.description}>{description}</p>
      <div className={styles.arrow}>
        <ArrowRightRegular />
      </div>
    </a>
  );
};
